import { tenantLocale } from '@/lib/locale'
import { useState } from 'react'
import { DataTable } from '@/components/ds/DataTable'
import { TrendingUp, DollarSign, Repeat, ClipboardList, BarChart3, BadgeDollarSign } from 'lucide-react'
import {
  ComposedChart, Bar, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts'
import { useForecast } from '../hooks/useSales'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { usePermission } from '@/hooks/usePermission'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTH_NAMES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']

const formatMonth = (ym: string) => {
  const [y, m] = ym.split('-')
  return `${MONTH_NAMES[parseInt(m) - 1]} ${y.slice(2)}`
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ForecastPage() {
  const { data, isLoading } = useForecast()
  const { isModuleEnabled } = usePermission()
  const commissionsEnabled = isModuleEnabled('commissions')

  const fmtMoney = (v: string | number) =>
    new Intl.NumberFormat(tenantLocale(), {
      style: 'currency',
      currency: data?.currency || 'CLP',
      maximumFractionDigits: 0,
    }).format(typeof v === 'string' ? parseFloat(v) : v)

  const fmtShort = (v: number) => {
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
    if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`
    return `$${v.toFixed(0)}`
  }

  // ── Loading skeleton ────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto animate-pulse">
        <div className="h-8 bg-surface-muted rounded w-64 mb-2" />
        <div className="h-4 bg-surface-muted rounded w-96 mb-6" />
        <div className="grid lg:grid-cols-6 sm:grid-cols-2 gap-4 mb-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-surface rounded-xl border border-divider p-4 h-20" />
          ))}
        </div>
        <div className="bg-surface rounded-xl border border-divider p-5 mb-6 h-96" />
        <div className="grid grid-cols-3 gap-4 mb-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-surface rounded-xl border border-divider p-4 h-16" />
          ))}
        </div>
        <div className="bg-surface rounded-xl border border-divider h-64" />
      </div>
    )
  }

  // ── Empty state ─────────────────────────────────────────────────────────────
  if (!data) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <PageHeader
          title="Proyección de Ventas"
          subtitle="Forecast basado en histórico, pipeline y recurrente"
          help={HELP['sales.forecast']}
        />
        <div className="flex items-center justify-center h-96">
          <p className="text-content-subtle text-sm">No hay datos suficientes para generar proyección</p>
        </div>
      </div>
    )
  }

  const { historical, forecast, kpis, summary_by_source } = data

  // ── Chart data ──────────────────────────────────────────────────────────────
  const chartData = [
    ...historical.map(h => ({
      month: formatMonth(h.month),
      'Histórico': parseFloat(h.total),
      'Comisión real': parseFloat(h.commission ?? '0'),
      type: 'historical' as const,
    })),
    ...forecast.map(f => ({
      month: formatMonth(f.month),
      Recurrente: parseFloat(f.recurring),
      Pipeline: parseFloat(f.pipeline),
      Tendencia: parseFloat(f.trend),
      'Comisión proy.': parseFloat(f.commission ?? '0'),
      type: 'forecast' as const,
    })),
  ]

  // Boundary label between historical and forecast
  const boundaryIndex = historical.length - 1
  const boundaryLabel = boundaryIndex >= 0 ? chartData[boundaryIndex]?.month : undefined

  // ── Growth rate styling ─────────────────────────────────────────────────────
  const growthRate = parseFloat(kpis.growth_rate)
  const growthPositive = growthRate >= 0

  // ── KPI definitions ─────────────────────────────────────────────────────────
  const kpiCards = [
    { label: 'Total Forecast 6m', value: fmtMoney(kpis.total_forecast), icon: DollarSign, bgCls: 'bg-primary-100', iconCls: 'text-primary' },
    { label: 'Promedio Mensual', value: fmtMoney(kpis.avg_monthly), icon: BarChart3, bgCls: 'bg-surface-muted', iconCls: 'text-content-muted' },
    { label: 'Recurrente', value: fmtMoney(kpis.recurring_revenue), icon: Repeat, bgCls: 'bg-success-surface-strong', iconCls: 'text-success-content-soft' },
    { label: 'Pipeline', value: fmtMoney(kpis.pipeline_value), icon: ClipboardList, bgCls: 'bg-primary-100', iconCls: 'text-primary' },
    {
      label: 'Crecimiento',
      value: `${growthPositive ? '+' : ''}${growthRate.toFixed(1)}%`,
      icon: TrendingUp,
      bgCls: growthPositive ? 'bg-success-surface-strong' : 'bg-danger-surface-strong',
      iconCls: growthPositive ? 'text-success-content-soft' : 'text-danger-content-soft',
    },
    ...(commissionsEnabled ? [{
      label: 'Comisiones 6m',
      value: fmtMoney(kpis.total_commission_forecast ?? '0'),
      icon: BadgeDollarSign,
      bgCls: 'bg-warning-surface-strong',
      iconCls: 'text-warning-content-soft',
    }] : []),
  ]

  // ── Source composition ──────────────────────────────────────────────────────
  const sources = [
    { label: 'Recurrente', pct: parseFloat(summary_by_source.recurring_pct), color: 'bg-success-solid' },
    { label: 'Pipeline', pct: parseFloat(summary_by_source.pipeline_pct), color: 'bg-primary' },
    { label: 'Tendencia', pct: parseFloat(summary_by_source.trend_pct), color: 'bg-content-subtle' },
  ]

  // ── Forecast totals for detail table ────────────────────────────────────────
  const totalRecurring = forecast.reduce((s, f) => s + parseFloat(f.recurring), 0)
  const totalPipeline = forecast.reduce((s, f) => s + parseFloat(f.pipeline), 0)
  const totalTrend = forecast.reduce((s, f) => s + parseFloat(f.trend), 0)
  const totalAll = forecast.reduce((s, f) => s + parseFloat(f.total), 0)

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* ── Header ───────────────────────────────────────────────────────────── */}
      <PageHeader
        title="Proyección de Ventas"
        subtitle="Forecast basado en histórico, pipeline y recurrente"
        help={HELP['sales.forecast']}
      />

      {/* ── KPI Cards ────────────────────────────────────────────────────────── */}
      <div className={`grid ${commissionsEnabled ? 'lg:grid-cols-6' : 'lg:grid-cols-5'} sm:grid-cols-2 gap-4 mb-6`}>
        {kpiCards.map(({ label, value, icon: Icon, bgCls, iconCls }) => (
          <div key={label} className="bg-glass backdrop-blur-glass rounded-card border border-divider p-4 shadow-card">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${bgCls} flex items-center justify-center`}>
                <Icon size={20} className={iconCls} />
              </div>
              <div>
                <p className="text-xs text-content-muted">{label}</p>
                <p className="text-lg font-bold text-content">{value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Main Chart ───────────────────────────────────────────────────────── */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 mb-6 shadow-card">
        <h2 className="text-base font-semibold text-content mb-1">Hist&oacute;rico y Proyecci&oacute;n</h2>
        <p className="text-xs text-content-subtle mb-4">
          6 meses de ventas reales + 6 meses proyectados
        </p>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--color-divider))" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'rgb(var(--color-content-muted))' }} stroke="rgb(var(--color-divider))" />
            <YAxis yAxisId="sales" tickFormatter={(v: number) => fmtShort(v)} tick={{ fill: 'rgb(var(--color-content-muted))' }} stroke="rgb(var(--color-divider))" />
            {commissionsEnabled && <YAxis yAxisId="commission" orientation="right" tickFormatter={(v: number) => fmtShort(v)} tick={{ fontSize: 10, fill: '#d97706' }} stroke="rgb(var(--color-divider))" />}
            <Tooltip
              formatter={(value: number) => fmtMoney(value)}
              contentStyle={{
                borderRadius: 8,
                border: '1px solid rgb(var(--color-divider))',
                backgroundColor: 'rgb(var(--color-popover))',
                color: 'rgb(var(--color-content))',
                fontSize: 13,
              }}
              labelStyle={{ color: 'rgb(var(--color-content))' }}
            />
            <Legend wrapperStyle={{ color: 'rgb(var(--color-content-muted))' }} />
            {boundaryLabel && (
              <ReferenceLine
                yAxisId="sales"
                x={boundaryLabel}
                stroke="rgb(var(--color-content-subtle))"
                strokeDasharray="6 4"
                label={{ value: 'Hoy', position: 'top', fontSize: 11, fill: 'rgb(var(--color-content-subtle))' }}
              />
            )}
            <Bar yAxisId="sales" dataKey="Histórico" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            <Area yAxisId="sales" dataKey="Recurrente" stackId="forecast" fill="#10b981" fillOpacity={0.6} stroke="#10b981" />
            <Area yAxisId="sales" dataKey="Pipeline" stackId="forecast" fill="#3b82f6" fillOpacity={0.4} stroke="#3b82f6" />
            <Area yAxisId="sales" dataKey="Tendencia" stackId="forecast" fill="#94a3b8" fillOpacity={0.3} stroke="#94a3b8" />
            {commissionsEnabled && <Line yAxisId="commission" dataKey="Comisión real" stroke="#d97706" strokeWidth={2} dot={{ r: 3, fill: '#d97706' }} connectNulls />}
            {commissionsEnabled && <Line yAxisId="commission" dataKey="Comisión proy." stroke="#d97706" strokeWidth={2} strokeDasharray="5 3" dot={false} connectNulls />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ── Source Composition ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {sources.map(({ label, pct, color }) => (
          <div key={label} className="bg-glass backdrop-blur-glass rounded-card border border-divider p-4 shadow-card">
            <p className="text-xs text-content-muted mb-1">{label}</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-surface-muted rounded-full h-2.5">
                <div
                  className={`${color} h-2.5 rounded-full`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-sm font-bold text-content">{pct.toFixed(1)}%</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── Detail Table ─────────────────────────────────────────────────────── */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden shadow-card">
        <div className="px-5 py-4 border-b border-divider">
          <h2 className="text-base font-semibold text-content">Detalle Mensual</h2>
        </div>
        <DataTable<typeof forecast[number]>
          columns={[
            { key: 'month', header: 'Mes', sortable: true,
              render: f => <span className="text-content">{formatMonth(f.month)}</span> },
            { key: 'recurring', header: 'Recurrente', numeric: true, sortable: true,
              sortValue: f => Number(f.recurring),
              render: f => <span className="text-content-muted">{fmtMoney(f.recurring)}</span> },
            { key: 'pipeline', header: 'Pipeline', numeric: true, sortable: true,
              sortValue: f => Number(f.pipeline),
              render: f => <span className="text-content-muted">{fmtMoney(f.pipeline)}</span> },
            { key: 'trend', header: 'Tendencia', numeric: true, sortable: true,
              sortValue: f => Number(f.trend),
              render: f => <span className="text-content-muted">{fmtMoney(f.trend)}</span> },
            { key: 'total', header: 'Total', numeric: true, sortable: true,
              sortValue: f => Number(f.total),
              render: f => <span className="font-medium text-content">{fmtMoney(f.total)}</span> },
            ...(commissionsEnabled ? [{
              key: 'commission', header: 'Comisión', numeric: true, sortable: true,
              sortValue: (f: typeof forecast[number]) => Number(f.commission ?? 0),
              render: (f: typeof forecast[number]) => (
                <span className="font-medium text-warning-content-soft">{fmtMoney(f.commission ?? '0')}</span>
              ),
            }] : []),
          ]}
          rows={forecast}
          rowKey={f => f.month}
          minWidth={720}
          surface={false}
          // ⚠️ El total estaba DENTRO del `<tbody>`, o sea era una fila más: al
          // ordenar por cualquier columna se habría mezclado entre los meses.
          // Como pie del componente queda fuera del cuerpo y hereda la alineación.
          footerLabel="Total"
          footer={{
            recurring: fmtMoney(totalRecurring),
            pipeline: fmtMoney(totalPipeline),
            trend: fmtMoney(totalTrend),
            total: fmtMoney(totalAll),
            ...(commissionsEnabled
              ? { commission: <span className="text-warning-content">{fmtMoney(kpis.total_commission_forecast ?? '0')}</span> }
              : {}),
          }}
          caption="Proyección de ingresos por mes"
        />
      </div>
    </div>
  )
}
